import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxButtonComponent, FlxIconModule } from '@flowx/angular-ui-toolkit'
import {
  FileDownloadTemplateConfig,
  LabelAccessibilityProps,
  ProcessActionTriggerEvent,
  ResolvedAction,
  getActionsByTemplateConfigAndTrigger$,
  getCurrentReadOnlyState,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxActionService } from '../../services/flx-action.service'
import { getBoolExpressionSignal, getContentIcon, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-download-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [FlxButtonComponent, FlxLocalizePipe, FlxThemeModule, FlxStyleModule, FlxIconModule],
  templateUrl: './download-wrapper.component.html',
  styleUrl: './download-wrapper.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxDownloadWrapperComponent implements OnInit, AfterViewInit {
  #actionService = inject(FlxActionService)
  #injector = inject(Injector)
  #destroyRef = inject(DestroyRef)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  // actions for the CLICK trigger – kept in sync with the process-actions store
  // so they arrive even when the node's details resolve after this component mounts.
  #clickActions = signal<ResolvedAction[]>([])

  templateConfig = input.required<FileDownloadTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)
  variant = computed(() => this.flowxProps().type || 'ghost')

  icons = computed(() => {
    const { icon } = this.flowxProps()
    return { icon: getContentIcon(icon) }
  })

  readonly = getCurrentReadOnlyState

  // expressions
  isHidden: Signal<boolean>
  isDisabled: Signal<boolean>
  // dynamic props
  label: Signal<string>
  ariaLabel: Signal<string>

  ngOnInit(): void {
    getActionsByTemplateConfigAndTrigger$(this.templateConfig(), ProcessActionTriggerEvent.CLICK)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#clickActions.set(actions))

    runInInjectionContext(this.#injector, () => {
      const { label, hide, disabled } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.isDisabled = getBoolExpressionSignal(disabled, this.templateConfig(), {
        logType: 'disabled',
      })
      this.label = getDynamicPropSignal(label, this.templateConfig())
      this.ariaLabel = getDynamicPropSignal(this.accessibility()?.label, this.templateConfig())
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }

  async onClick(): Promise<void> {
    await this.#actionService.runActions(this.#clickActions(), this.templateConfig())
  }
}
